import { useMemo } from 'react';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { IncidentListCard } from 'components/MapPage/CorporateViewTabs/IncidentsTab/IncidentListCard';
import { buildIncidentListItems } from 'components/MapPage/CorporateViewTabs/IncidentsTab/utils/buildIncidentListItems';
import { OrgEventListItem } from 'components/MapPage/CorporateViewTabs/EventsTab/OrgEventListItem';
import { useOrgEvents } from 'hooks/orgEvents/orgEventHooks';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { matchEntityListItems } from './matchEntityListItems';
import { resolveEntityListLive } from 'components/Reports/section/dynamicSectionSnapshot';

export type OrgEventsPayload = {
  events?: Array<{
    id: string;
    name: string;
    type: string | null;
    status: string;
    severity: string;
    started_at: string | null;
    last_update: string | null;
  }>;
  skippedMissingCoordinates?: number;
  scopeLabel?: string;
  error?: string;
  notes?: string[];
};

export type IncidentsPayload = {
  incidents?: Array<{
    id: string;
    name: string;
    incident_types: string[] | null;
    severity: string | null;
    match_reason: 'area' | 'aor';
    incident_created: string | null;
  }>;
  scopeLabel?: string;
  error?: string;
  notes?: string[];
};

export function parseEntityListPayload(
  raw: string | null | undefined
): unknown {
  if (!raw?.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function matchReasonLabel(reason: 'area' | 'aor'): string {
  return reason === 'aor' ? 'Assigned AOR' : 'In area';
}

export function OrgEventsResults({
  payload,
  emptyHint,
  useLiveEnrichment = true,
}: {
  payload: OrgEventsPayload | null;
  emptyHint: string;
  useLiveEnrichment?: boolean;
}) {
  const { data: orgEvents = [] } = useOrgEvents({ includeArchived: true });
  const liveById = useMemo(
    () => new Map(orgEvents.map((event) => [event.id, event])),
    [orgEvents]
  );
  const matched = useMemo(
    () => matchEntityListItems(payload?.events, liveById),
    [liveById, payload?.events]
  );

  if (!payload) {
    return <p className="tw:mb-0 tw:text-muted-foreground">{emptyHint}</p>;
  }
  if (payload.error) {
    return (
      <p className="tw:mb-0 tw:text-destructive">
        <strong>Error:</strong> {payload.error}
      </p>
    );
  }

  const notes = [...(payload.notes ?? [])];
  if ((payload.skippedMissingCoordinates ?? 0) > 0) {
    notes.push(
      `${payload.skippedMissingCoordinates} event(s) were skipped because they have no coordinates.`
    );
  }

  return (
    <div>
      {payload.scopeLabel ? (
        <p className="tw:mb-2 tw:text-sm">
          <strong>Area:</strong> {payload.scopeLabel}
        </p>
      ) : null}
      {matched.length === 0 ? (
        <p className="tw:mb-0 tw:text-muted-foreground">
          No events in this area.
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <p className="tw:mb-0 tw:text-sm">
            <strong>{matched.length} event(s)</strong>
          </p>
          {matched.map(({ payload: row, live }) =>
            resolveEntityListLive(live, useLiveEnrichment) ? (
              <OrgEventListItem
                key={row.id}
                event={live!}
                scrollOnOpen={false}
              />
            ) : (
              <p
                key={row.id}
                className="tw:mb-0 tw:rounded tw:border tw:border-border tw:px-3 tw:py-2 tw:text-sm"
              >
                {row.name}
              </p>
            )
          )}
        </div>
      )}
      {notes.length > 0 ? (
        <ul className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-muted-foreground">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function IncidentsResults({
  payload,
  emptyHint,
  useLiveEnrichment = true,
}: {
  payload: IncidentsPayload | null;
  emptyHint: string;
  useLiveEnrichment?: boolean;
}) {
  const aorText = useAorText();
  const { data: incidents = [] } = useAllIncidents();
  const { data: aors = [] } = useAors();
  const { data: orgEvents = [] } = useOrgEvents();

  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );
  const eventNameById = useMemo(
    () => new Map(orgEvents.map((event) => [event.id, event.name])),
    [orgEvents]
  );
  const listIncidents = useMemo(
    () =>
      buildIncidentListItems({
        incidents,
        aorNameById,
        eventNameById,
      }),
    [aorNameById, eventNameById, incidents]
  );
  const liveById = useMemo(
    () => new Map(listIncidents.map((incident) => [incident.id, incident])),
    [listIncidents]
  );
  const matched = useMemo(
    () => matchEntityListItems(payload?.incidents, liveById),
    [liveById, payload?.incidents]
  );

  if (!payload) {
    return <p className="tw:mb-0 tw:text-muted-foreground">{emptyHint}</p>;
  }
  if (payload.error) {
    return (
      <p className="tw:mb-0 tw:text-destructive">
        <strong>Error:</strong> {payload.error}
      </p>
    );
  }

  return (
    <div>
      {payload.scopeLabel ? (
        <p className="tw:mb-2 tw:text-sm">
          <strong>Area:</strong> {payload.scopeLabel}
        </p>
      ) : null}
      {matched.length === 0 ? (
        <p className="tw:mb-0 tw:text-muted-foreground">
          No incidents in this area.
        </p>
      ) : (
        <div className="d-grid gap-2">
          <p className="tw:mb-0 tw:text-sm">
            <strong>{matched.length} incident(s)</strong>
          </p>
          {matched.map(({ payload: row, live }) =>
            resolveEntityListLive(live, useLiveEnrichment) ? (
              <IncidentListCard
                key={row.id}
                incident={live!}
                defaultCollapsed
              />
            ) : (
              <p
                key={row.id}
                className="tw:mb-0 tw:rounded tw:border tw:border-border tw:px-3 tw:py-2 tw:text-sm"
              >
                {row.name}
                {row.match_reason ? (
                  <span className="tw:ml-2 tw:text-xs tw:text-muted-foreground">
                    {aorText(matchReasonLabel(row.match_reason))}
                  </span>
                ) : null}
              </p>
            )
          )}
        </div>
      )}
      {payload.notes?.length ? (
        <ul className="tw:mt-2 tw:mb-0 tw:text-xs tw:text-muted-foreground">
          {payload.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
